import {
  addNwsAndWildfiresLayers,
  updateNwsAndWildfiresFilters,
  removeNwsAndWildfiresLayers,
  setNwsAndWildfiresLayersVisibility,
  checkNwsAndWildfiresLayersVisible,
} from './configureNwsAndWildfiresLayers.js';
import {
  addCustomEventsLayers,
  removeCustomEventsLayers,
  setCustomEventsLayersVisibility,
} from './configureCustomEventsLayers.js';
import {
  addPowerOutageLayers,
  removePowerOutageLayers,
  setPowerOutageLayersVisibility,
} from './configurePowerOutageLayers.js';
import {
  addHurricaneLayers,
  updateHurricaneFilters,
  removeHurricaneLayers,
  setHurricaneLayersVisibility,
} from './configureHurricaneLayers.js';
import {
  addEarthquakeLayers,
  updateEarthquakeFilters,
  removeEarthquakeLayers,
  setEarthquakeLayersVisibility,
} from './configureEarthquakeLayers.js';
import {
  addMapEventHandlersMemo,
  addVectorSourceMemo,
  checkLayerExistsMemo,
  checkLayerToggleMemo,
  checkLayerVisibleMemo,
  removeLayerMemo,
  removeSourceMemo,
  setFilterMemo,
  setLayerVisibilityMemo,
} from './mapEventsEffectMemos.js';
import {
  MapMemo,
  LayerClickHandler,
  EarthquakeFeature,
} from './mapEventsEffectTypes.js';

import { Moment } from 'moment';

export type MapEventsEffectInput = {
  map: MapMemo;
  mapHasLoaded: boolean;
  eventsVisibleToggle: boolean;
  selectedDatetime: Moment;
  setLayerClicked: LayerClickHandler;
  earthquakesData: EarthquakeFeature[];
  poweroutageActive: boolean;
  eventToggles: any[];
  group_guid: UUID;
};

export const mapEventsEffect = ({
  map,
  mapHasLoaded,
  eventsVisibleToggle,
  selectedDatetime,
  setLayerClicked,
  earthquakesData,
  poweroutageActive,
  eventToggles,
  group_guid,
}: MapEventsEffectInput) => {
  if (!mapHasLoaded || !earthquakesData) return;

  const LAYER_REMOVAL_METHODS = [
    removeCustomEventsLayers,
    removeEarthquakeLayers,
    removeHurricaneLayers,
    removeNwsAndWildfiresLayers,
    removePowerOutageLayers,
  ];

  const LAYER_VISIBILITY_SETTERS = [
    setCustomEventsLayersVisibility,
    setEarthquakeLayersVisibility,
    setHurricaneLayersVisibility,
    setNwsAndWildfiresLayersVisibility,
    setPowerOutageLayersVisibility,
  ];

  /*
   If you try to remove a layer that doesn't exist, Mapbox GL JS will throw an error.
   To avoid this, we need to check if the layer exists before removing it.
   We are using a pattern to hide the implementation details of the Mapbox API.
   This will help enforce a check for the layer's existence.
   The goal is to make it safer to work with the Mapbox API.
   The factory pattern will also allow us to focus on the business logic that is found here.
  */
  const checkLayerToggle = checkLayerToggleMemo(eventToggles);

  const layerExists = checkLayerExistsMemo(map);

  const checkLayerVisible = checkLayerVisibleMemo(map);

  const setFilter = setFilterMemo(map);

  const addMapEventHandlers = addMapEventHandlersMemo(map, setLayerClicked);

  const addVectorSource = addVectorSourceMemo(map);

  const removeSource = removeSourceMemo(map);

  const removeLayer = removeLayerMemo(map);

  const setLayerVisibility = setLayerVisibilityMemo(map);

  // Only remove and recreate layers if they don't exist
  // Todo: This should really check all the known layers, right?
  const shouldCreateLayers =
    !checkNwsAndWildfiresLayersVisible(checkLayerVisible);

  if (shouldCreateLayers) {
    // Remove existing layers if they exist
    LAYER_REMOVAL_METHODS.forEach((removeLayerMethod) => {
      removeLayerMethod({ removeLayer, removeSource });
    });

    if (eventsVisibleToggle) {
      try {
        const formattedDatetime = selectedDatetime
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
        const endDatetime = selectedDatetime
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
        const formattedHoursDatetime = selectedDatetime
          .minute(0)
          .second(0)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');

        // NWS Alerts and Wildfires
        addNwsAndWildfiresLayers({
          addMapEventHandlers,
          addVectorSource,
          checkLayerToggle,
          endDatetime,
          formattedDatetime,
        });

        // Custom Events
        // Add custom polygon events tiles
        addCustomEventsLayers({
          addMapEventHandlers,
          addVectorSource,
          group_guid,
        });

        // Power Outage Layers
        if (poweroutageActive) {
          addPowerOutageLayers({
            addMapEventHandlers,
            addVectorSource,
            checkLayerToggle,
            formattedHoursDatetime,
          });
        }

        // Hurricane Layers
        addHurricaneLayers({
          addMapEventHandlers,
          addVectorSource,
          checkLayerToggle,
          endDatetime,
          formattedDatetime,
        });

        // Earthquakes Layers
        // note that earthquakes are configured differently due to the clustering mechanic
        addEarthquakeLayers({
          checkLayerToggle,
          earthquakesData,
          map,
          setLayerClicked,
        });
      } catch (e) {
        console.error('Error in mapEventsEffect:', e);
        return;
      }
    } else {
      // If layers exist, just update their visibility and filters

      // Update layer visibility
      LAYER_VISIBILITY_SETTERS.forEach((setLayerVisibilityMethod) => {
        setLayerVisibilityMethod({ setLayerVisibility, isVisible: false });
      });

      // Update NWS and Wildfires filters
      updateNwsAndWildfiresFilters(setFilter, eventToggles);

      // Update earthquake filters
      updateEarthquakeFilters(checkLayerToggle, setFilter);

      // Update hurricane filters
      updateHurricaneFilters(layerExists, checkLayerToggle, setFilter);
    }
  }
};
